import { Fragment, useMemo, useState } from "react";
import { ApiRequestError, getFields } from "../lib/client";
import type { FieldsResponse } from "../lib/types";
import { FieldType } from "../lib/types";

const COMMON_OBJECTS = [
  "Account",
  "Contact",
  "Opportunity",
  "Campaign",
  "User",
  "Task",
  "Event",
];

/** Browse a Salesforce object's describe metadata — the source of `field_name`s. */
export function FieldsExplorer({ subscriberId }: { subscriberId: string }) {
  const [objectName, setObjectName] = useState("Opportunity");
  const [includeRefs, setIncludeRefs] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [labelFilter, setLabelFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [fields, setFields] = useState<FieldsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggleExpanded(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  async function load() {
    const name = objectName.trim();
    if (!name) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getFields(subscriberId, name, {
        include_references: includeRefs,
        refresh,
      });
      setFields(data);
      setExpanded(new Set());
    } catch (err) {
      setFields(null);
      setError(
        err instanceof ApiRequestError ? err.message : "Failed to load fields.",
      );
    } finally {
      setLoading(false);
    }
  }

  const rows = useMemo(() => {
    if (!fields) return [];
    const q = labelFilter.trim().toLowerCase();
    return Object.entries(fields)
      .filter(
        ([key, meta]) =>
          (!q ||
            key.toLowerCase().includes(q) ||
            (meta.label ?? "").toLowerCase().includes(q)) &&
          (!typeFilter || meta.type === typeFilter),
      )
      .sort(([a], [b]) => a.localeCompare(b));
  }, [fields, labelFilter, typeFilter]);

  return (
    <section className="panel">
      <div className="toolbar">
        <input
          className="text-input"
          list="common-objects"
          placeholder="Object API name (e.g. Opportunity)"
          value={objectName}
          spellCheck={false}
          onChange={(e) => setObjectName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
        />
        <datalist id="common-objects">
          {COMMON_OBJECTS.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
        <label className="check">
          <input
            type="checkbox"
            checked={includeRefs}
            onChange={(e) => setIncludeRefs(e.target.checked)}
          />
          include references
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={refresh}
            onChange={(e) => setRefresh(e.target.checked)}
          />
          force refresh
        </label>
        <button
          type="button"
          className="btn primary"
          onClick={load}
          disabled={loading}
        >
          {loading ? "Loading…" : "Load fields"}
        </button>
      </div>

      {error && <div className="banner error">{error}</div>}

      {fields && (
        <>
          <div className="toolbar">
            <input
              className="text-input grow"
              placeholder={`Filter ${Object.keys(fields).length} fields by name or label…`}
              value={labelFilter}
              onChange={(e) => setLabelFilter(e.target.value)}
            />
            <select
              id="field-type-filter"
              className="select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All types</option>
              {Object.entries(FieldType).map(([k, v]) => (
                <option key={k} value={v}>
                  {v}
                </option>
              ))}
            </select>
            <span className="count">{rows.length} shown</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Label</th>
                  <th>API name (field_name)</th>
                  <th>Type</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([key, meta]) => {
                  const isRef =
                    (meta.referenceTo?.length ?? 0) > 0 ||
                    !!meta.relationshipName;
                  const picklistValues = meta.picklistValues ?? [];
                  const picklist = picklistValues.length;
                  const isExpanded = expanded.has(key);
                  return (
                    <Fragment key={key}>
                      <tr>
                        <td>{meta.label ?? "—"}</td>
                        <td>
                          <button
                            type="button"
                            className="copyable"
                            title="Click to copy"
                            onClick={() => navigator.clipboard?.writeText(key)}
                          >
                            {key}
                          </button>
                        </td>
                        <td>
                          <span className="tag">{meta.type}</span>
                        </td>
                        <td className="details">
                          {isRef && meta.referenceTo?.length ? (
                            <span className="tag ref">
                              → {meta.referenceTo.join(", ")}
                            </span>
                          ) : null}
                          {picklist > 0 && (
                            <button
                              className="tag ref"
                              type="button"
                              title={
                                isExpanded
                                  ? "Hide picklist values"
                                  : "Show picklist values"
                              }
                              aria-expanded={isExpanded}
                              onClick={() => toggleExpanded(key)}
                            >
                              {isExpanded ? "▾" : "▸"} {picklist} values
                            </button>
                          )}
                          {meta.custom && <span className="tag">custom</span>}
                        </td>
                      </tr>
                      {picklist > 0 && isExpanded && (
                        <tr className="picklist-row">
                          <td colSpan={4}>
                            <table className="picklist-table">
                              <thead>
                                <tr>
                                  <th>Label</th>
                                  <th>API name</th>
                                </tr>
                              </thead>
                              <tbody>
                                {picklistValues.map((pv) => (
                                  <tr
                                    key={pv.value}
                                    className={
                                      pv.active ? undefined : "inactive"
                                    }
                                  >
                                    <td>
                                      <span className="pv-label">
                                        {pv.label}
                                      </span>
                                    </td>
                                    <td>
                                      <code className="pv-value">
                                        {pv.value}
                                      </code>
                                      {!pv.active && (
                                        <span className="tag">inactive</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
